import { useEffect, useState } from 'react';

export interface GithubStats {
  days: number;
  commits: number;
  pullRequests: number;
  merged: number;
}

function isGithubStats(value: Partial<Record<keyof GithubStats, unknown>>): value is GithubStats {
  return (['days', 'commits', 'pullRequests', 'merged'] as const).every(key => typeof value[key] === 'number');
}

export type GithubStatsStatus = 'loading' | 'ready' | 'error';

export interface GithubStatsResult {
  stats: GithubStats | null;
  status: GithubStatsStatus;
}

/**
 * Recent GitHub activity from the site's /api/github-stats function, which searches with a
 * server-side token so private and organisation repositories count. The function is
 * CDN-cached for an hour. `stats` is null while loading or on failure; status is 'error' on failure,
 * including under `bun dev`, where Vite does not serve /api.
 */
export function useGithubStats(): GithubStatsResult {
  const [result, setResult] = useState<GithubStatsResult>({ stats: null, status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/github-stats', { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error(`/api/github-stats returned ${response.status}`);
        const body: Partial<Record<keyof GithubStats, unknown>> = await response.json();
        if (!isGithubStats(body)) throw new Error('/api/github-stats returned an unexpected shape');
        setResult({ stats: body, status: 'ready' });
      })
      .catch((error: Error) => {
        if (error.name === 'AbortError') return;
        console.warn('[useGithubStats] fetch failed, card shows no stats', error);
        setResult({ stats: null, status: 'error' });
      });
    return () => controller.abort();
  }, []);

  return result;
}
